/* @flow */
import findIndex from "lodash/findIndex";
import uniq from "lodash/uniq";

import type { Dispatch } from "../action";

export type Section = {
  key: string,
  uid: number,
  title: string,
  subtitle?: string
};

type Video = {
  duration: string,
  name: string,
  preview: string,
  url: string
};

export type HelpdeskBlock = {
  content: string,
  key: string,
  section: Section,
  tags: number,
  title: string,
  type: "text" | "video",
  uid: string,
  video: null | Video,
  weight: number
};

export type Slug = {
  id: number,
  slug: string,
  blocks: Array<HelpdeskBlock>
};

export type Locale =
  | null
  | "vi_VN"
  | "id_ID"
  | "fil_PH"
  | "ja_JP"
  | "th_TH"
  | "en_US"
  | "es_AR"
  | "ko_KR"
  | "ru_RU";

export type NewsItem = {
  slug: string,
  key: string,
  id: number,
  locale: string,
  title: string,
  teaser: string,
  teaserLogo?: string | null,
  createdAt: string,
  publishedAt?: null | string,
  media?: Array<{ path: string, link: string }>
};

type State = {
  watchedVideos: Array<string>,
  pages: Array<Slug>,
  news: Array<NewsItem> | null,
  sections: Array<Section>,
  search: null | Array<Slug>,
  search_loading: boolean
};

const initialState = {
  watchedVideos: [],
  pages: [],
  sections: [],
  news: null,
  search: null,
  search_loading: true
};

function remakeSlug(page: Slug) {
  const { id, blocks, slug } = page;

  const remadeBlocks = blocks.reduce(
    (obj, block) => {
      const { sections, blocks } = obj;
      const { section } = block;

      // если секция есть
      if (section != null && section.key != null) {
        if (section.title === `"`) {
          const firstLetter = block.title[1];
          section.key = `KB_${firstLetter}`;
          section.title = firstLetter;
          section.subject = `KB_${firstLetter}`;
        }

        // проверяем, есть ли она уже
        const doesSectionExist = !!sections.find(
          existing => existing.key === section.key
        );

        // если ее нет - создаем новую и пушим в новую
        if (doesSectionExist === false) {
          const key = section.key || "";
          const hash = `#${key.toLowerCase().replace(/\s/g, "")}`;
          sections.push({ ...section, hash, blocks: [block] });
        }

        // если она есть - вычисляем и пушим в имеющуюся
        if (doesSectionExist === true) {
          const sectionIdx = findIndex(sections, o => o.key === section.key);

          sections[sectionIdx]["blocks"].push(block);
        }
      } else {
        // если секции нет
        blocks.push(block);
      }

      return obj;
    },
    {
      blocks: [],
      sections: []
    }
  );

  return {
    id,
    slug,
    ...remadeBlocks
  };
}

function remakeKnowledgeBase(page: Slug) {
  const remadePage = remakeSlug(page);

  function sortContent(a: Object, b: Object) {
    const [titleA, titleB] = [a.title, b.title];

    return titleA.localeCompare(titleB);
  }

  const sections = remadePage.sections.sort(sortContent);

  const sortedSections = sections.reduce((acc, section) => {
    const sortedBlocks = section.blocks.sort(sortContent);

    acc.push({ ...section, blocks: sortedBlocks });
    return acc;
  }, []);

  return { ...remadePage, sections: sortedSections };
}

function remakeAllPages(pages: Array<Slug>) {
  const remadePages = pages.reduce((arr, page) => {
    const remadeSlug = remakeSlug(page);
    arr.push(remadeSlug);
    return arr;
  }, []);

  return remadePages;
}

function receiveWatched(state: State, payload) {
  let watched = [];
  try {
    const data = localStorage.getItem("watchedVideos");
    if (data) {
      const stored = JSON.parse(data);
      if (Array.isArray(stored)) {
        watched = stored;
      }
    }
  } catch (err) {
    console.log(err);
  }

  const watchedVideos = [...state.watchedVideos, ...watched];

  return {
    ...state,
    watchedVideos: uniq(watchedVideos)
  };
}

function updateWatched(state: State, payload: string) {
  const watchedVideos = [...state.watchedVideos, payload];

  return {
    ...state,
    watchedVideos: uniq(watchedVideos)
  };
}

function resetAllSlugs(state: State) {
  return {
    ...initialState,
    watchedVideos: state.watchedVideos
  };
}

function helpdesk(state: State = initialState, action: Dispatch) {
  const { type, payload } = action;

  switch (type) {
    case "RECEIVE_HELPDESK_PAGE":
      return {
        ...state,
        [payload["slug"]]:
          payload["slug"] === "knowledge_base"
            ? remakeKnowledgeBase(payload)
            : remakeSlug(payload),
        [`${payload["slug"]}_raw`]: payload
      };
    case "RECEIVE_HELPDESK_NEWS":
      return { ...state, news: payload };
    case "RECEIVE_HELPDESK_SEARCH_RESULTS":
      return {
        ...state,
        search_loading: false,
        search: payload
      };
    case "INVALIDATE_HELPDESK_SEARCH_RESULTS":
      return { ...state, search_loading: true, search: null };
    case "RECEIVE_HELPDESK_PAGES":
      return { ...state, pages: remakeAllPages(payload) };
    case "RECEIVE_HELPDESK_SECTIONS":
      return { ...state, sections: payload };
    case "RECEIVE_WATCHED_VIDEOS":
      return receiveWatched(state, payload);
    case "UPDATE_WATCHED_VIDEOS":
      return updateWatched(state, payload);
    case "INVALIDATE_PROFILE":
    case "UPDATE_LOCALE":
      return resetAllSlugs(state);
    default:
      return state;
  }
}

export default helpdesk;



// WEBPACK FOOTER //
// ./src/redux/reducer/helpdesk.js